/**
 * AdminApiActivity — per-token API send history and abuse flags for admins.
 *
 * Shows:
 *  • A timeline of recent API-originated email sends, filterable by user / token
 *  • Unresolved velocity-spike flags written by emailService.validateEmailRequest
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    collection,
    query,
    where,
    orderBy,
    limit,
    getDocs,
    Timestamp,
    updateDoc,
    doc,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { apiKeyService, ApiKey } from '../../services/apiKeyService';
import { AdminService, AdminUser } from '../../services/adminService';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import {
    ShieldExclamationIcon,
    CheckCircleIcon,
    ArrowPathIcon,
} from '@heroicons/react/24/outline';

interface EmailLogRow {
    id: string;
    userId: string;
    to: string | string[];
    subject?: string;
    apiKeyId?: string | null;
    source?: string | null;
    status: string;
    sentAt?: Timestamp | null;
    ipAddress?: string | null;
    provider?: string | null;
}

interface AbuseFlag {
    id: string;
    userId: string;
    apiKeyId?: string | null;
    type: string;
    detail: string;
    flaggedAt?: Timestamp | null;
    resolved: boolean;
}

const AdminApiActivity: React.FC = () => {
    const { isDark } = useAdminTheme();
    const { showSuccess, showError } = useNotification();

    const [logs, setLogs] = useState<EmailLogRow[]>([]);
    const [flags, setFlags] = useState<AbuseFlag[]>([]);
    const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
    const [users, setUsers] = useState<AdminUser[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filterUserId, setFilterUserId] = useState('');
    const [filterKeyId, setFilterKeyId] = useState('');

    const load = useCallback(async () => {
        setIsLoading(true);
        try {
            const [allKeys, allUsers] = await Promise.all([
                apiKeyService.getAllApiKeys(),
                AdminService.getAllUsers(),
            ]);
            setApiKeys(allKeys);
            setUsers(allUsers);

            // Recent API sends
            const logsSnap = await getDocs(
                query(
                    collection(db, 'email_logs'),
                    where('source', '==', 'api'),
                    orderBy('sentAt', 'desc'),
                    limit(500)
                )
            );
            setLogs(logsSnap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<EmailLogRow, 'id'>) })));

            // Unresolved abuse flags
            const flagsSnap = await getDocs(
                query(
                    collection(db, 'api_abuse_flags'),
                    where('resolved', '==', false),
                    orderBy('flaggedAt', 'desc'),
                    limit(100)
                )
            );
            setFlags(flagsSnap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<AbuseFlag, 'id'>) })));
        } catch (err) {
            console.error('AdminApiActivity load error:', err);
            showError('Failed to load API activity data');
        } finally {
            setIsLoading(false);
        }
    }, [showError]);

    useEffect(() => { load(); }, [load]);

    const userMap = useMemo(() => {
        const m = new Map<string, AdminUser>();
        users.forEach(u => m.set(u.id, u));
        return m;
    }, [users]);

    const keyMap = useMemo(() => {
        const m = new Map<string, ApiKey>();
        apiKeys.forEach(k => m.set(k.id, k));
        return m;
    }, [apiKeys]);

    const filteredLogs = useMemo(() => {
        return logs.filter(l => {
            if (filterUserId && l.userId !== filterUserId) return false;
            if (filterKeyId && l.apiKeyId !== filterKeyId) return false;
            return true;
        });
    }, [logs, filterUserId, filterKeyId]);

    const resolveFlag = async (flagId: string) => {
        try {
            await updateDoc(doc(db, 'api_abuse_flags', flagId), { resolved: true });
            setFlags(prev => prev.filter(f => f.id !== flagId));
            showSuccess('Flag marked as resolved.');
        } catch {
            showError('Failed to resolve flag.');
        }
    };

    const formatTs = (ts?: Timestamp | null) => {
        if (!ts) return '—';
        return ts.toDate().toLocaleString();
    };

    const formatRecipients = (to: string | string[]) => {
        if (Array.isArray(to)) {
            if (to.length === 1) return to[0];
            return `${to[0]} +${to.length - 1} more`;
        }
        return to;
    };

    const base = `px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`;
    const th = `px-4 py-3 text-xs font-semibold uppercase tracking-wider text-left ${isDark ? 'text-gray-400 bg-gray-900/50' : 'text-gray-500 bg-gray-50'}`;
    const card = `rounded-xl border overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-500" />
            </div>
        );
    }

    return (
        <div className={`p-6 space-y-8 ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>

            {/* ── Header ── */}
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold">API Activity</h2>
                    <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Per-token send history and abuse flags
                    </p>
                </div>
                <button
                    onClick={load}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-cyan-600 text-white hover:bg-cyan-700 transition-colors"
                >
                    <ArrowPathIcon className="w-4 h-4" />
                    Refresh
                </button>
            </div>

            {/* ── Abuse flags ── */}
            {flags.length > 0 && (
                <div className={`rounded-xl border overflow-hidden ${isDark ? 'border-red-900/50 bg-red-950/20' : 'border-red-200 bg-red-50'}`}>
                    <div className="px-5 py-3 flex items-center gap-2 border-b border-red-200/50 dark:border-red-900/50">
                        <ShieldExclamationIcon className="w-5 h-5 text-red-500" />
                        <span className="font-semibold text-red-700 dark:text-red-400">
                            {flags.length} unresolved abuse flag{flags.length !== 1 ? 's' : ''}
                        </span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr>
                                    <th className={th}>Flagged at</th>
                                    <th className={th}>User</th>
                                    <th className={th}>Token</th>
                                    <th className={th}>Type</th>
                                    <th className={th}>Detail</th>
                                    <th className={th}>Action</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-red-100'}`}>
                                {flags.map(f => {
                                    const u = userMap.get(f.userId);
                                    const k = f.apiKeyId ? keyMap.get(f.apiKeyId) : null;
                                    return (
                                        <tr key={f.id}>
                                            <td className={base}>{formatTs(f.flaggedAt)}</td>
                                            <td className={base}>
                                                <div className="font-medium">
                                                    {u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email : f.userId}
                                                </div>
                                                {u?.email && <div className="text-xs text-gray-500">{u.email}</div>}
                                            </td>
                                            <td className={base}>
                                                {k
                                                    ? <span className="font-mono text-xs">{k.keyPreview} — {k.name}</span>
                                                    : <span className="italic text-gray-400 text-xs">{f.apiKeyId || 'unknown'}</span>
                                                }
                                            </td>
                                            <td className={base}>
                                                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                                                    {f.type}
                                                </span>
                                            </td>
                                            <td className={`${base} max-w-xs`}>
                                                <p className="truncate">{f.detail}</p>
                                            </td>
                                            <td className={base}>
                                                <button
                                                    onClick={() => resolveFlag(f.id)}
                                                    className="inline-flex items-center gap-1 text-xs font-medium text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200"
                                                >
                                                    <CheckCircleIcon className="w-4 h-4" />
                                                    Resolve
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ── Filters ── */}
            <div className={`${card} p-4`}>
                <div className="flex flex-wrap gap-4">
                    <div className="flex-1 min-w-[200px]">
                        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Filter by user
                        </label>
                        <select
                            value={filterUserId}
                            onChange={e => setFilterUserId(e.target.value)}
                            className={`w-full text-sm rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500 ${isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-200'}`}
                        >
                            <option value="">All users</option>
                            {users.map(u => (
                                <option key={u.id} value={u.id}>
                                    {`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email} ({u.email})
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Filter by token
                        </label>
                        <select
                            value={filterKeyId}
                            onChange={e => setFilterKeyId(e.target.value)}
                            className={`w-full text-sm rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500 ${isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-200'}`}
                        >
                            <option value="">All tokens</option>
                            {apiKeys.map(k => (
                                <option key={k.id} value={k.id}>
                                    {k.name} — {k.keyPreview} {!k.isActive ? '(revoked)' : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                    {(filterUserId || filterKeyId) && (
                        <div className="flex items-end">
                            <button
                                onClick={() => { setFilterUserId(''); setFilterKeyId(''); }}
                                className="text-sm text-cyan-600 hover:underline"
                            >
                                Clear filters
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Send log ── */}
            <div className={card}>
                <div className={`px-5 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between`}>
                    <span className="font-semibold">
                        {filteredLogs.length} API send{filteredLogs.length !== 1 ? 's' : ''}
                        {(filterUserId || filterKeyId) ? ' (filtered)' : ' — last 500'}
                    </span>
                </div>
                {filteredLogs.length === 0 ? (
                    <p className="text-center py-12 italic text-gray-400">No API sends found matching these filters.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr>
                                    <th className={th}>Sent at</th>
                                    <th className={th}>User</th>
                                    <th className={th}>Token</th>
                                    <th className={th}>To</th>
                                    <th className={th}>Subject</th>
                                    <th className={th}>Status</th>
                                    <th className={th}>IP</th>
                                    <th className={th}>Provider</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-100'}`}>
                                {filteredLogs.map(l => {
                                    const u = userMap.get(l.userId);
                                    const k = l.apiKeyId ? keyMap.get(l.apiKeyId) : null;
                                    return (
                                        <tr key={l.id} className={isDark ? 'hover:bg-gray-700/40' : 'hover:bg-gray-50'}>
                                            <td className={base}>{formatTs(l.sentAt)}</td>
                                            <td className={base}>
                                                <div className="font-medium text-xs">
                                                    {u ? u.email : l.userId}
                                                </div>
                                            </td>
                                            <td className={base}>
                                                {k
                                                    ? <span className="font-mono text-xs">{k.keyPreview}</span>
                                                    : l.apiKeyId
                                                        ? <span className="font-mono text-xs text-gray-400">{l.apiKeyId.slice(0, 8)}…</span>
                                                        : <span className="italic text-gray-400 text-xs">—</span>
                                                }
                                            </td>
                                            <td className={base}>
                                                <span className="text-xs">{formatRecipients(l.to)}</span>
                                            </td>
                                            <td className={`${base} max-w-[200px]`}>
                                                <span className="block truncate text-xs">{l.subject || '—'}</span>
                                            </td>
                                            <td className={base}>
                                                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                                    l.status === 'sent'
                                                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                                }`}>
                                                    {l.status}
                                                </span>
                                            </td>
                                            <td className={base}>
                                                <span className="font-mono text-xs">{l.ipAddress || '—'}</span>
                                            </td>
                                            <td className={base}>
                                                <span className="text-xs">{l.provider || '—'}</span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminApiActivity;
